/**
 * Event positioning algorithms for Calendar views.
 * Handles layout in month view (day cells) and time grid (week/day views).
 */
import { parseISO, isValid } from 'date-fns';
import { isAllDayEvent, isMultiDayEvent } from './calendarDateUtils';

export interface CalendarEvent {
  id: string;
  title?: string;
  start: string;
  end?: string;
  allDay?: boolean;
  category?: string;
  [key: string]: unknown;
}

export interface DayEventLayout {
  id: string;
  title: string;
  slotIndex: number;
  isMultiDay: boolean;
  isAllDay: boolean;
  event: CalendarEvent;
}

export interface DayLayoutResult {
  visibleEvents: DayEventLayout[];
  overflowCount: number;
}

export interface TimeGridEventLayout {
  id: string;
  title: string;
  top: number;
  height: number;
  event: CalendarEvent;
}

const DEFAULT_COLOR = '#3b82f6';

/** Pre-computed palette for auto-assigning colors to categories */
const AUTO_PALETTE = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
  '#e11d48', '#84cc16', '#0ea5e9', '#d946ef', '#64748b',
];

/**
 * Position events within a day cell for month view.
 * All-day events render first, then timed events.
 * Returns visible events (capped at maxEventsPerDay) and overflow count.
 */
export function positionEventsInDay(
  events: CalendarEvent[],
  maxEventsPerDay: number,
): DayLayoutResult {
  if (events.length === 0) {
    return { visibleEvents: [], overflowCount: 0 };
  }

  // Separate all-day and timed events; all-day events go first
  const allDayEvents = events.filter(e => isAllDayEvent({ start: e.start, end: e.end, allDay: e.allDay }));
  const timedEvents = events.filter(e => !isAllDayEvent({ start: e.start, end: e.end, allDay: e.allDay }));

  // Sort timed events by start time
  timedEvents.sort((a, b) => a.start.localeCompare(b.start));

  const sorted = [...allDayEvents, ...timedEvents];
  const visible = sorted.slice(0, maxEventsPerDay);
  const overflow = Math.max(0, sorted.length - maxEventsPerDay);

  const visibleEvents: DayEventLayout[] = visible.map((event, index) => ({
    id: event.id,
    title: event.title || '(No title)',
    slotIndex: index,
    isMultiDay: isMultiDayEvent({ start: event.start, end: event.end }),
    isAllDay: isAllDayEvent({ start: event.start, end: event.end, allDay: event.allDay }),
    event,
  }));

  return { visibleEvents, overflowCount: overflow };
}

/**
 * Position events in a time grid (week/day view).
 * Each event gets a top (Y offset) and height in pixels.
 * Pixel calculation: 1 hour = intervalMinutes pixels (e.g., 60px for 60-min slots).
 */
export function positionEventsInTimeGrid(
  events: CalendarEvent[],
  intervalMinutes: number,
): TimeGridEventLayout[] {
  // The week/day views render on a fixed 60px-per-hour grid.
  // `intervalMinutes` changes slot density, not the absolute pixel scale.
  const pxPerMinute = 1;

  return events
    .filter(e => !isAllDayEvent({ start: e.start, end: e.end, allDay: e.allDay }))
    .map(event => {
      const startDate = parseISO(event.start);
      if (!isValid(startDate)) {
        return { id: event.id, title: event.title || '(No title)', top: 0, height: 60, event };
      }

      const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
      const top = startMinutes * pxPerMinute;

      let durationMinutes = 60; // Default 1 hour
      if (event.end) {
        const endDate = parseISO(event.end);
        if (isValid(endDate)) {
          const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();
          durationMinutes = endMinutes - startMinutes;
          if (durationMinutes <= 0) {
            // Overnight event: extend to midnight (end of day grid)
            durationMinutes = (24 * 60) - startMinutes;
          }
        }
      }

      const height = durationMinutes * pxPerMinute;

      return {
        id: event.id,
        title: event.title || '(No title)',
        top,
        height: Math.max(height, 20), // Minimum 20px
        event,
      };
    });
}

/**
 * Resolve an event's color based on its category and colorMap.
 * Falls back to an auto-generated palette color for unmapped categories.
 */
export function resolveEventColor(
  category: string | undefined,
  colorMap: Record<string, string> | undefined,
): string {
  if (!category) return DEFAULT_COLOR;
  if (colorMap && colorMap[category]) return colorMap[category];

  // Auto-generate a consistent color from the category string
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = ((hash << 5) - hash + category.charCodeAt(i)) | 0;
  }
  return AUTO_PALETTE[Math.abs(hash) % AUTO_PALETTE.length];
}
